import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { Eye, EyeOff, Mail, Lock, Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { firebaseAuth, isFirebaseConfigured, getFirebaseConfigError } from "../services/firebase";
import { redirectAfterAuth } from "../utils/postAuthRedirect";
import "./CSS/auth.css";
import cael from "./CSS/cael.png";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const { login, firebaseSocialLogin, isLoggingIn, resendVerification } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setError(null);

      if (!isFirebaseConfigured() || !firebaseAuth) {
        throw new Error(getFirebaseConfigError() || 'Firebase is not configured.');
      }

      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(firebaseAuth, provider);
      const firebaseIdToken = await cred.user.getIdToken();

      const result = await firebaseSocialLogin({
        firebaseIdToken,
        providerHint: 'google',
      });

      if (result.status === "success") {
        const from = location.state?.from?.pathname || "/";
        await redirectAfterAuth(navigate, from);
      } else {
        setError(result.message || t('login.errors.googleLoginFailed', { defaultValue: 'Google sign-in failed. Please try again.' }));
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup — no error message needed
        return;
      }
      setError(err.message || t('login.errors.googleLoginFailed', { defaultValue: 'Google sign-in failed. Please try again.' }));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // --- Legacy Google Identity Services (GIS) flow — replaced by Firebase popup above ---
  // const googleButtonRef = useRef(null);
  // const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '253094084760-np533giqhr976f7ujr5b46d2inqolksf.apps.googleusercontent.com';
  //
  // const handleGoogleSignInLegacy = async (response) => {
  //   try {
  //     setIsGoogleLoading(true);
  //     setError(null);
  //     if (!response.credential) throw new Error('No credential received from Google');
  //     const result = await socialLogin({ idToken: response.credential, provider: 'google' });
  //     if (result.status === "success") {
  //       const from = location.state?.from?.pathname || "/";
  //       navigate(from, { replace: true });
  //     } else {
  //       setError(result.message || t('login.errors.googleLoginFailed', { defaultValue: 'Google sign-in failed. Please try again.' }));
  //     }
  //   } catch (err) {
  //     console.error('Google sign-in error:', err);
  //     let errorMessage = err.message || t('login.errors.googleLoginFailed', { defaultValue: 'Google sign-in failed. Please try again.' });
  //     if (errorMessage.includes('origin_mismatch') || errorMessage.includes('origin') || errorMessage.includes('OAuth 2.0 policy')) {
  //       const currentOrigin = window.location.origin;
  //       errorMessage = `Google Sign-In configuration error: origin_mismatch. The current origin (${currentOrigin}) is not authorized in Google Cloud Console. Please add "${currentOrigin}" to the "Authorized JavaScript origins" in your Google OAuth 2.0 Client ID settings.`;
  //     }
  //     setError(errorMessage);
  //   } finally {
  //     setIsGoogleLoading(false);
  //   }
  // };
  //
  // useEffect(() => {
  //   if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
  //     if (window.google && window.google.accounts) {
  //       window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleSignInLegacy });
  //       if (googleButtonRef.current) {
  //         window.google.accounts.id.renderButton(googleButtonRef.current, { theme: 'outline', size: 'large', text: 'signin_with', width: '100%', type: 'standard' });
  //       }
  //     }
  //     return;
  //   }
  //   const script = document.createElement('script');
  //   script.src = 'https://accounts.google.com/gsi/client';
  //   script.async = true;
  //   script.defer = true;
  //   script.onload = () => {
  //     if (window.google && window.google.accounts) {
  //       window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleSignInLegacy });
  //       if (googleButtonRef.current) {
  //         window.google.accounts.id.renderButton(googleButtonRef.current, { theme: 'outline', size: 'large', text: 'signin_with', width: '100%', type: 'standard' });
  //       }
  //     }
  //   };
  //   document.head.appendChild(script);
  // }, [GOOGLE_CLIENT_ID]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Basic validation
    if (!formData.email.trim()) {
      setError(t('login.errors.emailRequired'));
      setIsSubmitting(false);
      return;
    }

    if (!formData.password.trim()) {
      setError(t('login.errors.passwordRequired'));
      setIsSubmitting(false);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError(t('login.errors.invalidEmail'));
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await login({
        email: formData.email.trim(),
        password: formData.password,
      });

      if (result.status === "success") {
        const from = location.state?.from?.pathname || "/";
        await redirectAfterAuth(navigate, from);
      } else {
        setError(result.message || t('login.errors.loginFailed'));
      }
    } catch (err) {
      // Handle specific error messages
      const errorMessage = err.message || t('login.errors.loginFailed');
      
      // Check for email verification errors
      if (errorMessage.includes("not verified") || errorMessage.includes("verification")) {
        setError(errorMessage);
        setEmailNotVerified(true); // Show resend button
      } else if (errorMessage.includes("Invalid") || errorMessage.includes("incorrect")) {
        setEmailNotVerified(false);
        setError(t('login.errors.invalidCredentials'));
      } else if (errorMessage.includes("timeout") || errorMessage.includes("network")) {
        setError(t('login.errors.timeout'));
      } else {
        setError(errorMessage);
        setEmailNotVerified(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email.trim()) {
      setError(t('login.errors.emailRequired'));
      return;
    }

    setIsResending(true);
    setError(null);
    try {
      const result = await resendVerification({ email: formData.email.trim() });
      if (result.status === "success") {
        setError(null);
        alert(t('login.verificationEmailSent', { defaultValue: 'Verification email sent! Please check your inbox.' }));
        setEmailNotVerified(false);
      } else {
        setError(result.message || t('login.errors.resendFailed', { defaultValue: 'Failed to resend verification email.' }));
      }
    } catch (err) {
      setError(err.message || t('login.errors.resendFailed', { defaultValue: 'Failed to resend verification email.' }));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-bg"></div>

      <div className="auth-card">
        <Link to="/" className="auth-logo-link">
          <img className="auth-logo" src={cael} alt="St Cael Vog" />
        </Link>

        <h2>{t('login.title')}</h2>
        <p className="auth-subtitle">{t('login.subtitle')}</p>

        {error && <div className="auth-error">{error}</div>}

        <div className="auth-social-buttons">
          <button
            className="auth-social-btn google"
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
          >
            {isGoogleLoading ? (
              <Loader2 size={18} className="spin" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {isGoogleLoading
              ? t('login.signingIn', { defaultValue: 'Signing in...' })
              : t('login.continueWithGoogle', { defaultValue: 'Continue with Google' })
            }
          </button>

          {/* Apple sign-in — disabled on web for now.
          <button className="auth-social-btn apple" type="button" disabled>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            {t('login.continueWithApple', { defaultValue: 'Continue with Apple' })}
            <span className="coming-soon-badge">{t('common.comingSoon', { defaultValue: 'Soon' })}</span>
          </button>
          */}
        </div>

        <div className="auth-divider">
          <hr />
          <span>{t('login.or', { defaultValue: 'OR' })}</span>
          <hr />
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-input-wrapper">
            <Mail size={18} className="auth-input-icon" />
            <input
              type="email"
              name="email"
              className="auth-input"
              placeholder={t('login.email')}
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              disabled={isSubmitting || isLoggingIn}
            />
          </div>
          
          <div className="auth-input-wrapper">
            <Lock size={18} className="auth-input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className="auth-input"
              placeholder={t('login.password')}
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              disabled={isSubmitting || isLoggingIn}
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('login.hidePassword', { defaultValue: 'Hide password' }) : t('login.showPassword', { defaultValue: 'Show password' })}
              disabled={isSubmitting || isLoggingIn}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="auth-form-actions">
            <Link to="/forgot-password" className="auth-forgot-link">
              {t('login.forgotPassword')}
            </Link>
          </div>

          {emailNotVerified && (
            <div className="auth-verification-notice">
              <p className="auth-verification-text">
                {t('login.emailNotVerified', { defaultValue: 'Your email is not verified. Please check your inbox for the verification email.' })}
              </p>
              <button
                type="button"
                className="auth-resend-btn"
                onClick={handleResendVerification}
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    {t('login.resending', { defaultValue: 'Sending...' })}
                  </>
                ) : (
                  t('login.resendVerification', { defaultValue: 'Resend Verification Email' })
                )}
              </button>
            </div>
          )}

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={isSubmitting || isLoggingIn}
          >
            {isSubmitting || isLoggingIn ? (
              <>
                <Loader2 size={18} className="spin auth-loader-icon" />
                {t('login.submitting')}
              </>
            ) : (
              t('login.submit')
            )}
          </button>
        </form>

        <p className="auth-footer">
          {t('login.dontHaveAccount')} <Link to="/signup">{t('login.signUp')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
